/**
 * HAN-378 — Image-count indicators for PLP product cards.
 *
 * Renders a segmented line overlaid on the bottom of the card image: one segment
 * ("pip") per available image, with the active segment highlighted. Shown only when
 * the card's gallery has 2+ visible slides; self-hides otherwise (single-image rule).
 *
 * Surface-agnostic: works on native Horizon card-gallery and SearchSpring cards
 * (assets/ss-client.js), since both render the same <slideshow-component> inside
 * a .card-gallery container.
 *
 * The element is rendered empty in markup (so it survives Horizon's hydration morph
 * and SearchSpring re-renders). This component:
 *   - builds one pip per visible slide, rebuilding whenever slides are added/removed
 *     or shown/hidden (a color-swatch swap can grow/shrink the gallery);
 *   - tracks the active slide via the slideshow's `slideshow:select` event and its
 *     `current` index. We rely on the slideshow's logical index rather than scroll
 *     position because the card slideshow runs in `infinite` mode, where navigation
 *     repositions content without changing the scroller's scrollLeft.
 */
class HanroImageIndicators extends HTMLElement {
  #slideshow = null;
  #scroller = null;
  #pips = [];
  #mutationObserver = null;
  #rafId = 0;

  connectedCallback() {
    const gallery = this.closest('.card-gallery');
    this.#slideshow = gallery?.querySelector('slideshow-component') || null;
    this.#scroller = this.#slideshow?.querySelector('slideshow-slides[ref="scroller"]') || null;
    if (!this.#slideshow || !this.#scroller) return;

    this.#build();

    this.#slideshow.addEventListener('slideshow:select', this.#handleSelect);

    // Rebuild pips when slides are added/removed (swatch swap growing/shrinking the
    // gallery) or shown/hidden (sibling collapse). Coalesced via rAF.
    this.#mutationObserver = new MutationObserver(this.#scheduleBuild);
    this.#mutationObserver.observe(this.#scroller, {
      childList: true,
      attributes: true,
      attributeFilter: ['hidden'],
    });
  }

  disconnectedCallback() {
    this.#slideshow?.removeEventListener('slideshow:select', this.#handleSelect);
    this.#mutationObserver?.disconnect();
    cancelAnimationFrame(this.#rafId);
    this.#mutationObserver = null;
  }

  // Public: rebuild pips to match the current slide set. Called by
  // hanro-product-swatches.js after a color-swatch swap grows/shrinks the gallery.
  refresh() {
    this.#build();
  }

  #visibleSlides() {
    if (!this.#scroller) return [];
    return Array.from(this.#scroller.querySelectorAll(':scope > slideshow-slide')).filter(
      (slide) => !slide.hasAttribute('hidden')
    );
  }

  #scheduleBuild = () => {
    cancelAnimationFrame(this.#rafId);
    this.#rafId = requestAnimationFrame(() => this.#build());
  };

  #build() {
    const slides = this.#visibleSlides();

    // Only show indicators when there are 2+ images to page through.
    if (slides.length < 2) {
      this.removeAttribute('data-ready');
      this.replaceChildren();
      this.#pips = [];
      return;
    }

    const fragment = document.createDocumentFragment();
    this.#pips = slides.map(() => {
      const pip = document.createElement('span');
      pip.className = 'hanro-image-indicators__pip';
      fragment.appendChild(pip);
      return pip;
    });
    this.replaceChildren(fragment);
    this.setAttribute('data-ready', '');

    this.#setActive(this.#currentIndex());
  }

  #currentIndex() {
    const index = typeof this.#slideshow?.current === 'number' ? this.#slideshow.current : 0;
    return this.#clamp(index);
  }

  #clamp(index) {
    if (!this.#pips.length) return 0;
    return Math.max(0, Math.min(index, this.#pips.length - 1));
  }

  #setActive(index) {
    this.#pips.forEach((pip, i) => pip.classList.toggle('is-active', i === index));
  }

  #handleSelect = (event) => {
    const index = event?.detail?.index;
    if (typeof index !== 'number' || !this.#pips.length) return;
    this.#setActive(this.#clamp(index));
  };
}

if (!customElements.get('hanro-image-indicators')) {
  customElements.define('hanro-image-indicators', HanroImageIndicators);
}
